import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { useOrg } from '../context/OrgContext'
import { getBreachEvents } from '../api/endpoints'
import LoadingSpinner from '../components/LoadingSpinner'

const RiskTrends: React.FC = () => {
  const { currentOrg } = useOrg()
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d'>('30d')

  const { data: events, isLoading } = useQuery({
    queryKey: ['breach-events', currentOrg?.id],
    queryFn: () => getBreachEvents(currentOrg!.id),
    enabled: !!currentOrg,
  })

  // Calculate date range
  const getDaysAgo = () => {
    switch (dateRange) {
      case '7d':
        return 7
      case '30d':
        return 30
      case '90d':
        return 90
    }
  }

  // Process data for chart
  const chartData = React.useMemo(() => {
    if (!events) return []

    const daysAgo = getDaysAgo()
    const now = new Date()
    const startDate = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)

    // Filter events within range
    const filteredEvents = events.filter(
      (e) => new Date(e.created_at) >= startDate
    )

    // Group by date
    const groupedByDate: Record<string, { scores: number[]; count: number }> = {}

    filteredEvents.forEach((event) => {
      const date = new Date(event.created_at).toISOString().split('T')[0]
      if (!groupedByDate[date]) {
        groupedByDate[date] = { scores: [], count: 0 }
      }
      groupedByDate[date].scores.push(event.risk_score)
      groupedByDate[date].count++
    })

    // Calculate averages
    return Object.entries(groupedByDate)
      .map(([date, data]) => ({
        date,
        avgRiskScore: Math.round(
          data.scores.reduce((sum, score) => sum + score, 0) / data.scores.length
        ),
        breachCount: data.count,
      }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [events, dateRange])

  if (!currentOrg || isLoading) {
    return <LoadingSpinner fullPage message="Loading risk trends..." />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">Risk Score Trends</h1>
          <p className="text-text-muted mt-1">Track risk score changes over time</p>
        </div>

        <div className="flex gap-2">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setDateRange(range)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                dateRange === range
                  ? 'bg-accent-primary text-white'
                  : 'bg-background-tertiary text-text-muted hover:text-text-primary'
              }`}
            >
              {range === '7d' ? '7 Days' : range === '30d' ? '30 Days' : '90 Days'}
            </button>
          ))}
        </div>
      </div>

      {/* Average Risk Score Chart */}
      <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-6">Average Risk Score Over Time</h2>

        {chartData.length === 0 ? (
          <p className="text-text-muted text-center py-12">No data available for selected range</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="date"
                stroke="#737373"
                tick={{ fill: '#a3a3a3' }}
                tickFormatter={(value) => {
                  const date = new Date(value)
                  return `${date.getMonth() + 1}/${date.getDate()}`
                }}
              />
              <YAxis
                stroke="#737373"
                tick={{ fill: '#a3a3a3' }}
                domain={[0, 100]}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3f3f46',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: '#f5f5f5' }}
                itemStyle={{ color: '#3b82f6' }}
              />
              <Line
                type="monotone"
                dataKey="avgRiskScore"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 4 }}
                name="Avg Risk Score"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Breach Count Chart */}
      <div className="bg-background-secondary border border-zinc-700 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-text-primary mb-6">Breach Events Over Time</h2>

        {chartData.length === 0 ? (
          <p className="text-text-muted text-center py-12">No data available for selected range</p>
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
              <XAxis
                dataKey="date"
                stroke="#737373"
                tick={{ fill: '#a3a3a3' }}
                tickFormatter={(value) => {
                  const date = new Date(value)
                  return `${date.getMonth() + 1}/${date.getDate()}`
                }}
              />
              <YAxis stroke="#737373" tick={{ fill: '#a3a3a3' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3f3f46',
                  borderRadius: '8px',
                }}
                labelStyle={{ color: '#f5f5f5' }}
                itemStyle={{ color: '#f97316' }}
              />
              <Line
                type="monotone"
                dataKey="breachCount"
                stroke="#f97316"
                strokeWidth={2}
                dot={{ fill: '#f97316', r: 4 }}
                name="Breach Count"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  )
}

export default RiskTrends
